"use client";

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import {
	AlertTriangle,
	CheckCircle,
	Download,
	Images,
	Loader,
	Play,
	Settings,
	Upload,
	X,
	XCircle,
	Archive,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FileDropZone } from "@/components/FileDropZone";
import { useAccessibility } from "@/hooks/useAccessibility";
import {
	useImageWorkerPool,
	type ImageFile,
} from "@/hooks/useImageWorkerPool";
import { FORMAT, FILE_STATUS } from "@/constants/image-converter";

// --- Output format options ---

const OUTPUT_FORMATS = [
	{ value: FORMAT.JPEG, translationKey: "jpeg" },
	{ value: FORMAT.PNG, translationKey: "png" },
	{ value: FORMAT.WEBP, translationKey: "webp" },
	// AVIF disabled until WASM is rebuilt with avif muxer (see build/build-ffmpeg.sh)
	// { value: FORMAT.AVIF, translationKey: "avif" },
	{ value: FORMAT.TIFF, translationKey: "tiff" },
	{ value: FORMAT.BMP, translationKey: "bmp" },
	{ value: FORMAT.GIF, translationKey: "gif" },
] as const;

const QUALITY_MIN = 5;
const QUALITY_MAX = 100;
const QUALITY_DEFAULT = 85;
const PNG_MAX_COMPRESSION = 9;
const LOSSLESS_QUALITY = 100;

// --- Status icon per file ---

function FileStatusIcon({ status }: { status: ImageFile["status"] }) {
	switch (status) {
		case FILE_STATUS.PROCESSING:
			return (
				<Loader
					className="w-4 h-4 animate-spin text-yellow-500"
					aria-label="Processing"
				/>
			);
		case FILE_STATUS.COMPLETED:
			return (
				<CheckCircle
					className="w-4 h-4 text-green-600"
					aria-label="Completed"
				/>
			);
		case FILE_STATUS.ERROR:
			return (
				<XCircle className="w-4 h-4 text-red-500" aria-label="Error" />
			);
		default:
			return null;
	}
}

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Component ---

const ImageConverter = () => {
	const t = useTranslations("imageConverter");
	const {
		announce,
		announceProgress,
		announceError,
		announceSuccess,
	} = useAccessibility();

	const {
		files,
		isProcessing,
		isReady,
		isLoading,
		overallProgress,
		completedCount,
		totalCount,
		error,
		addFiles,
		removeFile,
		clearFiles,
		startConversion,
		cancelConversion,
		downloadAll,
		downloadFile,
	} = useImageWorkerPool();

	const [outputFormat, setOutputFormat] = useState<string>(FORMAT.JPEG);
	const [quality, setQuality] = useState(QUALITY_DEFAULT);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const qualityDisabled =
		outputFormat === FORMAT.BMP || outputFormat === FORMAT.GIF;

	// --- Quality description ---

	const getQualityDescription = (format: string, value: number): string => {
		switch (format) {
			case FORMAT.JPEG:
			case FORMAT.JPG:
				return t("quality.jpeg", { value });
			case FORMAT.PNG: {
				const effort = Math.round(
					((LOSSLESS_QUALITY - value) / LOSSLESS_QUALITY) * PNG_MAX_COMPRESSION,
				);
				return t("quality.pngCompression", { effort });
			}
			case FORMAT.WEBP:
				return value >= LOSSLESS_QUALITY
					? t("quality.webpLossless")
					: t("quality.webp", { value });
			case FORMAT.AVIF:
				return t("quality.avif", { value });
			case FORMAT.HEIC:
			case FORMAT.HEIF:
				return t("quality.heic", { value });
			case FORMAT.TIFF:
			case FORMAT.TIF:
				return t("quality.tiff", { value });
			case FORMAT.BMP:
				return t("quality.bmp");
			case FORMAT.GIF:
				return t("quality.gif");
			default:
				return t("quality.default", { value });
		}
	};

	// --- Status message ---

	const getStatusMessage = (): string => {
		if (error) return error;
		if (isLoading) return t("status.loading");
		if (isProcessing)
			return t("status.converting", {
				completed: completedCount,
				total: totalCount,
			});
		if (isReady) return t("status.ready");
		if (files.length > 0)
			return t("status.filesSelected", { count: files.length });
		return t("status.idle");
	};

	const statusReady = isReady || files.length > 0;

	// --- Handlers ---

	const handleFiles = useCallback(
		(files: FileList | File[]) => {
			addFiles(files);
			announce(t("a11y.filesSelected", { count: Array.from(files).length }));
		},
		[addFiles, announce, t],
	);

	const handleConvert = useCallback(() => {
		startConversion(outputFormat, quality);
		announce(t("a11y.conversionStarted"), "polite");
	}, [startConversion, outputFormat, quality, announce, t]);

	const handleCancel = useCallback(() => {
		cancelConversion();
		announce(t("a11y.conversionCancelled"));
	}, [cancelConversion, announce, t]);

	const handleDownloadAll = useCallback(async () => {
		await downloadAll();
		announce(t("a11y.downloadingZip", { count: completedCount }));
	}, [downloadAll, completedCount, announce, t]);

	const handleDownloadFile = useCallback(
		(id: string) => {
			downloadFile(id);
			announce(t("a11y.downloadStarted"));
		},
		[downloadFile, announce, t],
	);

	// Announce progress updates
	const prevProgress = useRef(0);
	if (isProcessing && overallProgress !== prevProgress.current) {
		prevProgress.current = overallProgress;
		announceProgress(overallProgress, 100, "Image conversion");
	}

	// Announce completion
	const wasProcessing = useRef(false);
	if (wasProcessing.current && !isProcessing && completedCount > 0) {
		announceSuccess(t("a11y.conversionComplete", { count: completedCount }));
	}
	wasProcessing.current = isProcessing;

	if (error) {
		announceError(error);
	}

	const filesLabel =
		completedCount === 1 ? t("download.file") : t("download.files");

	if (!mounted) {
		return (
			<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<Images
								className="h-8 w-8 me-4 text-destructive"
								aria-hidden="true"
							/>
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>
				</div>
			</div>
		);
	}

	return (
		<ErrorBoundary>
			<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<Images
								className="h-8 w-8 me-4 text-destructive"
								aria-hidden="true"
							/>
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>

					{/* Status Bar */}
					<Alert
						className={`mb-6 ${statusReady ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}
					>
						<AlertTriangle className="h-4 w-4" aria-hidden="true" />
						<AlertDescription className="font-bold">
							{getStatusMessage()}
						</AlertDescription>
					</Alert>

					<div className="flex flex-col gap-6">
						{/* Step 1: File Selection */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Upload className="w-6 h-6" aria-hidden="true" />
									{t("steps.selectImages")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<FileDropZone
									accept="image/*,.heic,.heif,.avif,.webp,.tiff,.tif,.bmp"
									multiple
									disabled={isProcessing}
									onFiles={handleFiles}
									label={t("selection.dropZone")}
									activeLabel={t("selection.dragging")}
									ariaLabel={t("selection.fileInputLabel")}
								/>

								{/* File list */}
								{files.length > 0 && (
									<div className="mt-4 space-y-2">
										<div className="flex items-center justify-between">
											<p className="text-sm font-bold text-muted-foreground">
												{t("selection.imagesSelected", {
													count: files.length,
												})}
											</p>
											<Button
												variant="ghost"
												size="sm"
												onClick={clearFiles}
												disabled={isProcessing}
											>
												{t("selection.clearAll")}
											</Button>
										</div>
										<div className="max-h-60 overflow-y-auto space-y-1 border rounded-md p-2">
											{files.map((file) => (
												<div
													key={file.id}
													className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/50 text-sm"
												>
													<Badge
														variant="secondary"
														className="text-xs font-mono uppercase"
													>
														{file.inputFormat}
													</Badge>
													<span className="flex-1 truncate font-medium">
														{file.name}
													</span>
													<span className="text-muted-foreground text-xs">
														{formatFileSize(file.size)}
													</span>
													<FileStatusIcon status={file.status} />
													{file.error && (
														<span
															className="text-red-500 text-xs truncate max-w-32"
															title={file.error}
														>
															{file.error}
														</span>
													)}
													<Button
														variant="ghost"
														size="sm"
														className="h-6 w-6 p-0"
														onClick={() => removeFile(file.id)}
														disabled={isProcessing}
														aria-label={t("selection.removeFile", {
															name: file.name,
														})}
													>
														<X className="w-3 h-3" />
													</Button>
												</div>
											))}
										</div>
									</div>
								)}

								{files.length === 0 && (
									<p className="mt-2 text-sm text-muted-foreground font-bold">
										{t("selection.emptyHint")}
									</p>
								)}
							</CardContent>
						</Card>

						{/* Step 2: Output Format */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Images className="w-6 h-6" aria-hidden="true" />
									{t("steps.chooseFormat")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<label htmlFor="output-format-select" className="sr-only">
									{t("format.selectLabel")}
								</label>
								<Select
									value={outputFormat}
									onValueChange={setOutputFormat}
									disabled={isProcessing}
								>
									<SelectTrigger
										id="output-format-select"
										className="w-full"
									>
										<SelectValue
											placeholder={t("format.selectPlaceholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										{OUTPUT_FORMATS.map((fmt) => (
											<SelectItem key={fmt.value} value={fmt.value}>
												{t(`format.${fmt.translationKey}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</CardContent>
						</Card>

						{/* Step 3: Quality Settings */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Settings className="w-6 h-6" aria-hidden="true" />
									{t("steps.qualitySettings")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<Label
									htmlFor="quality-slider"
									className="text-sm font-bold"
								>
									{t("quality.label", { value: quality })}
								</Label>
								<Slider
									id="quality-slider"
									value={[quality]}
									onValueChange={([v]) => setQuality(v)}
									min={QUALITY_MIN}
									max={QUALITY_MAX}
									step={1}
									className="mt-2"
									disabled={isProcessing || qualityDisabled}
									aria-label={t("quality.ariaLabel")}
								/>
								<p className="mt-2 text-sm text-muted-foreground font-bold">
									{getQualityDescription(outputFormat, quality)}
								</p>
							</CardContent>
						</Card>

						{/* Convert / Cancel Button */}
						<div className="flex justify-center gap-4">
							{!isProcessing ? (
								<Button
									onClick={handleConvert}
									size="lg"
									variant={files.length > 0 ? "default" : "outline"}
									disabled={files.length === 0 || isProcessing}
									className="px-8 py-4 text-lg"
								>
									<Play className="w-5 h-5 me-3" aria-hidden="true" />
									{files.length > 0
										? t("convert.button", { count: files.length })
										: t("convert.buttonEmpty")}
								</Button>
							) : (
								<Button
									onClick={handleCancel}
									size="lg"
									variant="destructive"
									className="px-8 py-4 text-lg"
								>
									<X className="w-5 h-5 me-3" aria-hidden="true" />
									{t("convert.cancel")}
								</Button>
							)}
						</div>

						{/* Progress Bar */}
						{isProcessing && (
							<Card className="p-3 sm:p-6">
								<div className="flex justify-between items-center mb-4">
									<Badge
										variant="secondary"
										className="font-black text-lg"
									>
										{t("progress.convertingTo", {
											format: outputFormat.toUpperCase(),
										})}
									</Badge>
									<span className="text-lg font-black">
										{overallProgress}%
									</span>
								</div>
								<Progress
									value={overallProgress}
									className="mb-4"
									aria-label={`${overallProgress}%`}
								/>
								<div className="text-sm font-bold text-center text-muted-foreground">
									{t("progress.complete", {
										completed: completedCount,
										total: totalCount,
									})}
								</div>
							</Card>
						)}

						{/* Step 4: Download Results */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Download className="w-6 h-6" aria-hidden="true" />
									{t("steps.downloadResults")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								{completedCount > 0 ? (
									<div className="space-y-4">
										{/* Download All as ZIP */}
										<div className="flex justify-center">
											<Button
												onClick={handleDownloadAll}
												size="lg"
												className="px-6 py-3"
											>
												<Archive
													className="w-5 h-5 me-3"
													aria-hidden="true"
												/>
												{t("download.downloadAllZip", {
													count: completedCount,
													filesLabel,
												})}
											</Button>
										</div>

										{/* Individual file downloads */}
										<div className="border-t pt-4 space-y-2 max-h-80 overflow-y-auto">
											{files
												.filter(
													(f) =>
														f.status === FILE_STATUS.COMPLETED &&
														f.outputUrl,
												)
												.map((file) => (
													<div
														key={file.id}
														className="flex items-center gap-3 py-2 px-3 rounded hover:bg-muted/50"
													>
														{file.outputUrl && (
															<img
																src={file.outputUrl}
																alt={file.outputName || file.name}
																className="w-12 h-12 object-cover rounded border"
															/>
														)}
														<span className="flex-1 truncate text-sm font-medium">
															{file.outputName}
														</span>
														<Button
															variant="outline"
															size="sm"
															onClick={() =>
																handleDownloadFile(file.id)
															}
														>
															<Download
																className="w-4 h-4 me-1"
																aria-hidden="true"
															/>
															{t("download.download")}
														</Button>
													</div>
												))}
										</div>
									</div>
								) : (
									<div className="text-center min-h-[8rem] flex items-center justify-center">
										{isProcessing ? (
											<p className="text-primary font-bold text-lg">
												{t("download.processing")}
											</p>
										) : (
											<p className="text-muted-foreground font-bold">
												{t("download.empty")}
											</p>
										)}
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Privacy Notice */}
					<Alert className="mt-8">
						<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
						<AlertDescription className="font-bold">
							{t.rich("privacy", {
								code: (chunks) => (
									<code className="bg-muted p-1 rounded font-mono text-sm">
										{chunks}
									</code>
								),
							})}
						</AlertDescription>
					</Alert>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default ImageConverter;
